import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import SpeechRecognition from 'react-speech-recognition';
import type { RemirrorJSON } from '@remirror/core';
import {
  ActiveChatSessionContext,
  SubmitMessageContext,
  ChatSessionInputContext,
} from '@trinity/chat-components/components/Chat/context';
import { useSpeechRecognition } from '@trinity/chat-components/components/Chat/hooks/useSpeechRecognition';
import type { RemirrorChatInputRef } from '@trinity/chat-components/components/Chat/RemirrorInput/RemirrorChatInput';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useTelemetryLogger } from '@trinity/observability-web/react';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import {
  TextToSpeechStateContext,
  VoiceModeContext,
  useLoadingVoiceClipContextNullSafe,
  useMicrophoneStateContextNullSafe,
  useStopTextToSpeechContextNullSafe,
} from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { useTimeout } from '@trinity/chat-components/hooks/useTimeout';
import { capitalize } from '@trinity/chat-components/utils';
import { requestMicrophonePermissionIfNeeded } from '@trinity/chat-components/utils/microphone';
import { InputControlsContext } from './context';

type Props = {
  children: React.ReactNode;
};

export const InputControlsContainer = (props: Props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const remirrorRef = useRef<RemirrorChatInputRef>(null);
  const soundLevelRef = useRef<HTMLDivElement>(null);
  const input = useContext(ChatSessionInputContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const stopTextToSpeech = useStopTextToSpeechContextNullSafe();
  const audioState = useContext(TextToSpeechStateContext);
  const voicemode = useContext(VoiceModeContext);
  const isVoiceModeEnabled = voicemode?.enabled ?? false;
  const { state, onSubmit: handleSubmit } = useContext(SubmitMessageContext);
  const loadingVoiceClipAudioPlayer = useLoadingVoiceClipContextNullSafe();
  const chat = useContext(ActiveChatSessionContext);
  const loggerCtx = useTelemetryLogger();
  const [__micStateCtx, setMicStateCtx] = useMicrophoneStateContextNullSafe();

  const apiClient = useChatApiClient();

  const previousChatIdentifierRef = useRef<string>(chat?.identifier);
  const hasInitializedRef = useRef<boolean>(false);
  const userHasSpokenRef = useRef<boolean>(false);
  const [userHasSpoken, setUserHasSpoken] = useState<boolean>(false);
  const wasTTSPlayingRef = useRef<boolean>(false);
  const suppressInputUntilRef = useRef<number>(0);

  const setUserHasSpokenState = useCallback((value: boolean): void => {
    userHasSpokenRef.current = value;
    setUserHasSpoken(value);
  }, []);

  const captureAsyncError = useCallback(
    (error: unknown, operation: string): void => {
      loggerCtx.captureException(error, {
        tags: {
          component: 'InputControls.Container',
          operation,
        },
      });
    },
    [loggerCtx]
  );

  const loadingVoiceClipAudioPlayerStart = useCallback(() => {
    apiClient.chat
      .fetchLoadingAudioClip()
      .then((audioUrl) => {
        loadingVoiceClipAudioPlayer.onPlay(audioUrl);
      })
      .catch((error: unknown) => {
        captureAsyncError(error, 'fetchLoadingAudioClip');
        return undefined;
      });
  }, [apiClient.chat, loadingVoiceClipAudioPlayer, captureAsyncError]);

  const submitSuppressionWindowMs = 1000;

  const clearTranscriptInput = useCallback(() => {
    setUserHasSpokenState(false);
    remirrorRef.current?.clearAndSetText('');
  }, [setUserHasSpokenState]);

  const submitAndClearTranscript = useCallback(async (operation: string) => {
    suppressInputUntilRef.current = Date.now() + submitSuppressionWindowMs;

    if (isVoiceModeEnabled) {
      loadingVoiceClipAudioPlayerStart();
    }

    const submitPromise = Promise.resolve(handleSubmit());
    clearTranscriptInput();

    try {
      await submitPromise;
    } catch (error: unknown) {
      captureAsyncError(error, `${operation}.submit`);
    }
  }, [
    isVoiceModeEnabled,
    loadingVoiceClipAudioPlayerStart,
    handleSubmit,
    clearTranscriptInput,
    captureAsyncError,
  ]);

  const handleVoiceModeAutoSubmit = useCallback(() => {
    if (isVoiceModeEnabled && state.status === 'inactive') {
      Promise.resolve(submitAndClearTranscript('handleVoiceModeAutoSubmit')).catch((error: unknown) => {
        captureAsyncError(error, 'handleVoiceModeAutoSubmit.execute');
        return undefined;
      });
    }
  }, [isVoiceModeEnabled, state.status, submitAndClearTranscript, captureAsyncError]);

  const [, setTimer, clearTimer] = useTimeout(handleVoiceModeAutoSubmit, 200);

  const onSpeechFinished = useCallback(
    (value: string, prefix: string) => {
      if (Date.now() < suppressInputUntilRef.current) return;

      const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
      let newVal = safePrefix ? `${safePrefix} ${capitalize(value)}` : capitalize(value);

      if (!endsWithPunctuation(newVal)) {
        newVal = newVal + '?';
      }

      remirrorRef.current?.clearAndSetText(newVal);

      if (isVoiceModeEnabled) setTimer();
    },
    [setTimer, isVoiceModeEnabled]
  );

  const onAudioLevelChange = useCallback((level: number) => {
    if (!soundLevelRef.current) return;
    soundLevelRef.current?.setAttribute('style', `height: ${Math.min(20, Math.max(level, 4))}px`);
  }, []);

  const onInterimResult = useCallback(
    (result: string, prefix: string) => {
      if (Date.now() < suppressInputUntilRef.current) return;

      setUserHasSpokenState(true);

      const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
      const value = safePrefix ? `${safePrefix} ${capitalize(result)}` : capitalize(result);
      remirrorRef.current?.clearAndSetText(value);
      clearTimer();
    },
    [clearTimer, setUserHasSpokenState]
  );

  const {
    start,
    stop,
    listening,
    isSupported: speechRecogSupported,
    micState,
    errorMsg,
    interimResult,
  } = useSpeechRecognition({
    continuous: true,
    timeout: isVoiceModeEnabled ? 3000 : undefined,
    onResult: onSpeechFinished,
    onInterimResult,
    onAudioLevelChange,
    voicemode: isVoiceModeEnabled,
    onVoiceModeAutoSubmit: handleVoiceModeAutoSubmit,
  });

  const onVoiceClick = useCallback(() => {
    const startVoiceCaptureWithModeSync = async () => {
      const granted = await requestMicrophonePermissionIfNeeded();
      if (!granted) {
        return;
      }

      const safeInput = input && typeof input === 'string' ? input : '';
      try {
        await Promise.resolve(start(safeInput));
      } catch (error: unknown) {
        captureAsyncError(error, 'startVoiceCaptureWithModeSync.start');
      }
    };

    if (listening) {
      clearTimer();
      Promise.resolve(stop()).catch((error: unknown) => {
        captureAsyncError(error, 'onVoiceClick.stop');
        return undefined;
      });
    } else {
      Promise.resolve(startVoiceCaptureWithModeSync())
        .catch((error: unknown) => {
          captureAsyncError(error, 'onVoiceClick.startVoiceCaptureWithModeSync');
          return undefined;
        });
    }
  }, [
    clearTimer,
    listening,
    input,
    start,
    stop,
    captureAsyncError,
  ]);

  const handleSubmitButtonClick = useCallback(() => {
    const submitFromButton = async () => {
      clearTimer();
      if (listening) {
        try {
          await Promise.resolve(stop());
        } catch (error: unknown) {
          captureAsyncError(error, 'handleSubmitButtonClick.stop');
        }
      }

      await submitAndClearTranscript('handleSubmitButtonClick');
    };

    Promise.resolve(submitFromButton()).catch((error: unknown) => {
      captureAsyncError(error, 'handleSubmitButtonClick.execute');
      return undefined;
    });
  }, [
    clearTimer,
    listening,
    stop,
    submitAndClearTranscript,
    captureAsyncError,
  ]);

  const handleVoiceModeClick = useCallback(() => {
    clearTimer();

    if (!voicemode) {
      return;
    }

    if (isVoiceModeEnabled) {
      if (listening) {
        Promise.resolve(stop()).catch((error: unknown) => {
          captureAsyncError(error, 'handleVoiceModeClick.stop');
          return undefined;
        });
      }
      voicemode.disable();
      setMicStateCtx('inactive');
    } else {
      if (audioState?.state === 'playing' || audioState?.state === 'loading') {
        Promise.resolve(stopTextToSpeech()).catch((error: unknown) => {
          captureAsyncError(error, 'handleVoiceModeClick.stopTextToSpeech');
          return undefined;
        });
        loadingVoiceClipAudioPlayer.onStop();
      }

      if (!listening) {
        voicemode.enable();

        setUserHasSpokenState(false);

        const safeInput = input && typeof input === 'string' ? input : '';
        Promise.resolve(start(safeInput)).catch((error: unknown) => {
          captureAsyncError(error, 'handleVoiceModeClick.start');
          return undefined;
        });
      }
    }
  }, [
    audioState,
    input,
    clearTimer,
    listening,
    start,
    stop,
    voicemode,
    stopTextToSpeech,
    loadingVoiceClipAudioPlayer,
    isVoiceModeEnabled,
    setMicStateCtx,
    captureAsyncError,
    setUserHasSpokenState,
  ]);

  const stopTtsDebounceRef = useRef<NodeJS.Timeout>(null);

  const shouldStopPlayback = useMemo(() => {
    const isPlaying = audioState?.state === 'playing' || audioState?.state === 'loading';
    return isPlaying && isVoiceModeEnabled && listening && userHasSpoken;
  }, [audioState?.state, listening, isVoiceModeEnabled, userHasSpoken]);

  useEffect(() => {
    if (shouldStopPlayback) {
      if (stopTtsDebounceRef.current) clearTimeout(stopTtsDebounceRef.current);
      stopTtsDebounceRef.current = setTimeout(() => {
        if (stopTextToSpeech) {
          stopTextToSpeech();
        }
        loadingVoiceClipAudioPlayer.onStop();
      }, 75);
    }
  }, [
    shouldStopPlayback,
    stopTextToSpeech,
    loadingVoiceClipAudioPlayer,
    audioState?.state,
    isVoiceModeEnabled,
    listening,
  ]);

  useEffect(() => {
    const currentChatIdentifier = chat?.identifier;
    const previousChatIdentifier = previousChatIdentifierRef.current;

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      previousChatIdentifierRef.current = currentChatIdentifier;
      return;
    }

    if (previousChatIdentifier && currentChatIdentifier !== previousChatIdentifier) {
      suppressInputUntilRef.current = Date.now() + 1500;

      clearTimer();
      Promise.resolve(stop()).catch((error: unknown) => {
        captureAsyncError(error, 'chatChange.stop');
        return undefined;
      });
      try {
        Promise.resolve(SpeechRecognition.abortListening()).catch((error: unknown) => {
          captureAsyncError(error, 'chatChange.abortListening');
          return undefined;
        });
      } catch (error) {
        console.warn('SpeechRecognition.abortListening() not available:', error);
      }
      setMicStateCtx('inactive');

      if (voicemode?.enabled) voicemode.disable();

      if (stopTextToSpeech) {
        Promise.resolve(stopTextToSpeech(true)).catch((error: unknown) => {
          captureAsyncError(error, 'chatChange.stopTextToSpeech');
          return undefined;
        });
      }

      setUserHasSpokenState(false);

      setMicStateCtx('inactive');

      dispatch({ type: 'input-changed', payload: { value: '' } });
    }

    previousChatIdentifierRef.current = currentChatIdentifier;
  }, [
    chat?.identifier,
    listening,
    stop,
    clearTimer,
    voicemode,
    stopTextToSpeech,
    dispatch,
    setMicStateCtx,
    captureAsyncError,
    setUserHasSpokenState,
  ]);

  useEffect(() => {
    if (isTtsActive(audioState)) {
      wasTTSPlayingRef.current = true;
    }

    if (
      shouldRestartMicrophone(isVoiceModeEnabled, wasTTSPlayingRef.current, audioState, listening, micState)
    ) {
      wasTTSPlayingRef.current = false;
      setUserHasSpokenState(false);
      Promise.resolve(start(getSafeInput(input))).catch((error: unknown) => {
        captureAsyncError(error, 'restartMicrophoneAfterTts');
        return undefined;
      });
    }
    if (isTtsInactive(audioState)) {
      wasTTSPlayingRef.current = false;
    }
  }, [
    audioState,
    isVoiceModeEnabled,
    listening,
    micState,
    start,
    input,
    captureAsyncError,
    setUserHasSpokenState,
  ]);

  const insertTextAtCursor = useCallback((text: string) => {
    remirrorRef.current?.insertText(text);
  }, []);

  const replaceInput = useCallback((text: string) => {
    remirrorRef.current?.clearAndSetText(text);
  }, []);

  const replaceInputDoc = useCallback((doc: RemirrorJSON) => {
    remirrorRef.current?.clearAndSetDoc(doc);
  }, []);

  const value = {
    insertTextAtCursor,
    replaceInput,
    replaceInputDoc,
    onClickSubmit: handleSubmitButtonClick,
    onClickMicrophone: onVoiceClick,
    onClickVoiceMode: handleVoiceModeClick,
    speechRecognition: {
      errorMsg,
      interimResult,
      isSupported: speechRecogSupported,
      listening,
      micState,
      start,
      stop,
    },
    soundLevelRef,
    textAreaRef,
    remirrorRef,
  };

  return <InputControlsContext.Provider value={value}>{props.children}</InputControlsContext.Provider>;
};

InputControlsContainer.displayName = 'InputControls.Container';

function endsWithPunctuation(str: string) {
  const punctuation = /[.,!?;:'"-]/;
  return punctuation.test(str.charAt(str.length - 1));
}

function isTtsActive(audioState: { state: string | null } | null | undefined): boolean {
  return audioState?.state === 'playing' || audioState?.state === 'loading';
}

function isTtsInactive(audioState: { state: string | null } | null | undefined): boolean {
  return audioState?.state === 'stopped' || audioState?.state === 'paused' || audioState?.state === null;
}

function shouldRestartMicrophone(
  voicemodeEnabled: boolean,
  wasTtsPlaying: boolean,
  audioState: { state: string | null } | null | undefined,
  listening: boolean,
  micState: string
): boolean {
  const ttsStopped = wasTtsPlaying && isTtsInactive(audioState);
  return voicemodeEnabled && ttsStopped && !listening && micState === 'inactive';
}

function getSafeInput(input: unknown): string {
  return input && typeof input === 'string' ? input : '';
}
